import React, { useState } from "react";
import "./SectionExpertise.scss";

const isKorean = () => {
  const lang = localStorage.getItem("i18nextLng") || "en";
  return lang === "kr" || lang.startsWith("ko");
};

// ── Small tab icons (inherit accent via currentColor) ──
const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const icons: Record<string, JSX.Element> = {
  BIM: (
    <svg {...iconProps}>
      <path d="M12 3 L20 7 L12 11 L4 7 Z" />
      <path d="M4 12 L12 16 L20 12" />
      <path d="M4 16.5 L12 20.5 L20 16.5" />
    </svg>
  ),
  CAD: (
    <svg {...iconProps}>
      <circle cx="12" cy="5" r="1.6" />
      <path d="M12 6.6 V3" />
      <path d="M11.1 6.2 L7 20.5" />
      <path d="M12.9 6.2 L17 20.5" />
      <path d="M9.4 15 h5.2" />
    </svg>
  ),
  "3D VIZ": (
    <svg {...iconProps}>
      <path d="M12 2.5 L20 7 L12 11.5 L4 7 Z" />
      <path d="M4 7 V16 L12 20.5 V11.5" />
      <path d="M20 7 V16 L12 20.5" />
    </svg>
  ),
  MEDIA: (
    <svg {...iconProps}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M10 9.5 L14.5 12 L10 14.5 Z" />
    </svg>
  ),
};

// ── Large expert visualizations (one bespoke scene per discipline) ──
const visualProps = {
  viewBox: "0 0 320 220",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.3,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const visuals: Record<string, JSX.Element> = {
  // BIM — stacked information-model with data nodes
  BIM: (
    <svg {...visualProps}>
      <path d="M160 26 L250 60 L160 94 L70 60 Z" opacity="0.95" />
      <path d="M70 90 L160 124 L250 90" />
      <path d="M70 120 L160 154 L250 120" />
      <path d="M70 150 L160 184 L250 150" />
      <path d="M160 26 V184 M70 60 V150 M250 60 V150" opacity="0.35" />
      <circle cx="160" cy="26" r="3.5" fill="currentColor" />
      <circle cx="250" cy="60" r="3.5" fill="currentColor" />
      <circle cx="70" cy="60" r="3.5" fill="currentColor" />
      <circle cx="160" cy="124" r="3" fill="currentColor" opacity="0.7" />
      <path d="M278 50 h26 M278 64 h18 M278 78 h26 M278 92 h14" opacity="0.5" />
      <path d="M16 64 h28 M16 78 h18 M16 92 h28 M16 106 h14" opacity="0.5" />
    </svg>
  ),
  // CAD — blueprint: plan outline, circles, dimensions, crosshair
  CAD: (
    <svg {...visualProps}>
      <path d="M48 60 H190 V120 H150 V172 H48 Z" />
      <circle cx="232" cy="96" r="40" />
      <circle cx="232" cy="96" r="22" opacity="0.5" />
      <path d="M232 40 V152 M176 96 H288" opacity="0.4" />
      <path d="M48 188 H190 M48 182 V194 M190 182 V194" opacity="0.55" />
      <path d="M32 60 V172 M26 60 H38 M26 172 H38" opacity="0.55" />
      <path d="M150 120 l18 18 M168 130 v8 h-8" opacity="0.6" />
    </svg>
  ),
  // 3D VIZ — wireframe cube + perspective floor grid + rendered face
  "3D VIZ": (
    <svg {...visualProps}>
      <path d="M20 200 L300 200 M70 160 L250 160 M100 130 L220 130" opacity="0.3" />
      <path d="M60 200 L120 130 M160 200 L160 130 M260 200 L200 130" opacity="0.3" />
      <path d="M160 34 L236 74 L160 114 L84 74 Z" fill="currentColor" opacity="0.12" />
      <path d="M160 34 L236 74 L160 114 L84 74 Z" />
      <path d="M84 74 V150 L160 190 V114" />
      <path d="M236 74 V150 L160 190" />
      <path d="M160 114 V190" opacity="0.45" />
      <circle cx="236" cy="74" r="3" fill="currentColor" />
    </svg>
  ),
  // MEDIA — film frame + timeline + waveform + keyframes
  MEDIA: (
    <svg {...visualProps}>
      <rect x="60" y="40" width="200" height="104" rx="4" />
      <path d="M60 58 h200 M60 126 h200" opacity="0.4" />
      <path d="M74 40 v18 M104 40 v18 M134 40 v18 M164 40 v18 M194 40 v18 M224 40 v18 M254 40 v18" opacity="0.4" />
      <path d="M74 126 v18 M104 126 v18 M134 126 v18 M164 126 v18 M194 126 v18 M224 126 v18 M254 126 v18" opacity="0.4" />
      <circle cx="160" cy="92" r="20" />
      <path d="M153 82 L173 92 L153 102 Z" fill="currentColor" />
      <path
        d="M60 178 q10 -18 20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0"
        opacity="0.5"
      />
      <path d="M96 176 l6 6 -6 6 -6 -6 Z M160 176 l6 6 -6 6 -6 -6 Z M224 176 l6 6 -6 6 -6 -6 Z" fill="currentColor" opacity="0.7" />
    </svg>
  ),
};

interface Capability {
  index: string;
  label: string;
  titleEn: string;
  titleKr: string;
  descEn: string;
  descKr: string;
  tags: string[];
  tools: string[];
  accent: string;
}

const capabilities: Capability[] = [
  {
    index: "01",
    label: "BIM",
    titleEn: "Building Information Modeling",
    titleKr: "건축·건설 정보 모델링",
    descEn:
      "Built on Revit-based authoring, we unify the entire building lifecycle — design, construction and maintenance — into a single data-rich 3D model. From quantity take-off and clash detection to construction sequencing, our BIM workflow turns complex projects into precise, reliable decisions.",
    descKr:
      "Revit 기반 저작을 중심으로, 설계부터 시공·유지관리까지 건축물의 전 생애주기를 하나의 데이터 기반 3D 모델로 통합합니다. 물량 산출, 간섭 검토, 공정 시뮬레이션까지 — 복잡한 프로젝트를 정확하고 신뢰할 수 있는 의사결정으로 이어갑니다.",
    tags: ["Revit Authoring", "LOD 300–400", "Clash Detection", "4D Scheduling", "IFC · openBIM"],
    tools: ["Revit", "Navisworks", "Civil 3D", "Solibri"],
    accent: "#deff72",
  },
  {
    index: "02",
    label: "CAD",
    titleEn: "2D / 3D Drafting",
    titleKr: "2D·3D 설계 및 도면",
    descEn:
      "Precision 2D and 3D drafting and technical documentation for every discipline. From permit drawings to detailed shop and fabrication drawings, we deliver standards-compliant, accurate documents that connect design intent smoothly to site and production.",
    descKr:
      "각 분야에 최적화된 2D·3D 정밀 도면과 기술 문서를 제작합니다. 인허가 도면부터 상세 시공도, 제조 도면까지 — 표준과 정확성을 갖춘 도면으로 설계 의도를 현장과 생산까지 매끄럽게 연결합니다.",
    tags: ["Permit Drawings", "Shop Drawings", "Fabrication", "AutoCAD", "DWG · DXF"],
    tools: ["AutoCAD", "MicroStation", "Inventor", "DraftSight"],
    accent: "#ff72b4",
  },
  {
    index: "03",
    label: "3D VIZ",
    titleEn: "Rendering & Digital Twin",
    titleKr: "3D 렌더링 · 디지털 트윈",
    descEn:
      "Photoreal rendering and real-time visualization let clients experience a space before it is built. We go further into smart-city and digital-twin environments — 3D worlds synchronized with reality to support operation, monitoring and simulation.",
    descKr:
      "포토리얼 렌더링과 실시간 비주얼라이제이션으로 완공 전에 공간을 미리 경험하게 합니다. 나아가 스마트시티·디지털 트윈 구축까지, 현실과 동기화된 3D 환경으로 운영·모니터링·시뮬레이션을 지원합니다.",
    tags: ["Photoreal Render", "Real-time", "Digital Twin", "Smart City", "Unreal · Unity"],
    tools: ["Unreal", "3ds Max", "Twinmotion", "V-Ray"],
    accent: "#c872ff",
  },
  {
    index: "04",
    label: "MEDIA",
    titleEn: "VFX · Motion · Film",
    titleKr: "VFX · 모션 · 영상",
    descEn:
      "A broad media capability spanning motion graphics, VFX, live-action compositing and film content production. We blend footage and CG into polished, high-impact films that carry your brand and message with cinematic quality.",
    descKr:
      "모션그래픽, VFX, 실사 합성, 영상 콘텐츠 제작까지 폭넓은 미디어 역량을 갖추고 있습니다. 촬영 이미지와 CG를 정교하게 합성하여, 브랜드와 메시지를 영화적 완성도의 임팩트 있는 영상으로 전달합니다.",
    tags: ["Motion Graphics", "VFX", "Compositing", "Color Grade", "Film · Edit"],
    tools: ["After Effects", "Nuke", "Cinema 4D", "Premiere"],
    accent: "#ffb872",
  },
];

// ── Featured work: Smart City master film + digital twin ──
interface FeaturedVideo {
  id: number;
  label: string;
  subEn: string;
  subKr: string;
  thumb: string;
}

const FEATURED: FeaturedVideo[] = [
  {
    id: 1148402083,
    label: "Smart City",
    subEn: "Master Film",
    subKr: "스마트시티 마스터 필름",
    thumb:
      "https://i.vimeocdn.com/video/2098582612-9741c5719cba87064146320db9f99c3e6e81a6e65a149b009af713d003688dd9-d_640x360?region=us",
  },
  {
    id: 1148402295,
    label: "Smart City · Digital Twin",
    subEn: "Digital Twin",
    subKr: "디지털 트윈",
    thumb:
      "https://i.vimeocdn.com/video/2098582875-a226329c809a1005ffce799344030f2149a0ad67082025de02337600e8547279-d_640x360?region=us",
  },
];

function SectionExpertise() {
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const kr = isKorean();
  const active = capabilities[selectedIdx];

  return (
    <section className="section-expertise">
      <div className="se-container">
        <div className="se-header">
          <span className="se-header__label">EXPERTISE</span>
          <span className="se-header__sub">{kr ? "전문 분야" : "What we do"}</span>
        </div>

        <div className="se-intro">
          <h2 className="se-intro__title">
            We build in <span className="se-intro__accent">3D</span>.
          </h2>
          <p className="se-intro__lead">
            {kr
              ? "BIM · Revit · CAD 기반의 3D 비주얼라이제이션 · 미디어 콘텐츠 전문기업"
              : "A 3D Visualization & Media Studio — powered by BIM, Revit & CAD"}
          </p>
        </div>

        <div className="se-body">
          {/* ── Left: capability tabs ── */}
          <div className="se-tabs">
            {capabilities.map((cap, idx) => {
              const isActive = idx === selectedIdx;
              return (
                <button
                  type="button"
                  key={cap.label}
                  className={`se-tab ${isActive ? "se-tab--active" : ""}`}
                  style={{ "--accent": cap.accent } as React.CSSProperties}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  onClick={() => setSelectedIdx(idx)}
                >
                  <span className="se-tab__icon">{icons[cap.label]}</span>
                  <span className="se-tab__text">
                    <span className="se-tab__label">{cap.label}</span>
                    <span className="se-tab__title">{kr ? cap.titleKr : cap.titleEn}</span>
                  </span>
                  <span className="se-tab__index">{cap.index}</span>
                </button>
              );
            })}
          </div>

          {/* ── Right: expert detail panel ── */}
          <div
            className="se-detail"
            key={active.label}
            style={{ "--accent": active.accent } as React.CSSProperties}
          >
            <div className="se-detail__visual">
              <div className="se-detail__frame">
                <span className="se-detail__coord se-detail__coord--tl">
                  FIG. {active.index}
                </span>
                <span className="se-detail__coord se-detail__coord--tr">{active.label}</span>
                <span className="se-detail__coord se-detail__coord--bl">ISO · 1:100</span>
                <span className="se-detail__coord se-detail__coord--br">X / Y / Z</span>
                {visuals[active.label]}
              </div>
              <span className="se-detail__caption">
                FIG. {active.index} — {active.label} WORKFLOW REFERENCE
              </span>
            </div>

            <div className="se-detail__content">
              <div className="se-detail__head">
                <span className="se-detail__index">{active.index}</span>
                <h3 className="se-detail__label">{active.label}</h3>
              </div>
              <p className="se-detail__title">{kr ? active.titleKr : active.titleEn}</p>
              <p className="se-detail__desc">{kr ? active.descKr : active.descEn}</p>

              <span className="se-detail__meta">{kr ? "핵심 역량" : "CORE CAPABILITIES"}</span>
              <div className="se-detail__tags">
                {active.tags.map((t) => (
                  <span className="se-tag" key={t}>
                    {t}
                  </span>
                ))}
              </div>

              <div className="se-detail__toolkit">
                <span className="se-detail__meta">{kr ? "주요 툴" : "TOOLKIT"}</span>
                <div className="se-tools">
                  {active.tools.map((t) => (
                    <span className="se-tool" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Featured work: Smart City ── */}
        <div className="se-featured">
          <div className="se-featured__head">
            <span className="se-featured__label">FEATURED WORK</span>
          </div>
          <div className="se-featured__grid">
            {FEATURED.map((f) => (
              <div className="se-featured__card" key={f.id}>
                <div className="se-featured__media">
                  {playingId === f.id ? (
                    <iframe
                      title={f.label}
                      src={`https://player.vimeo.com/video/${f.id}?autoplay=1&title=0&byline=0&portrait=0`}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      className="se-featured__thumb"
                      onClick={() => setPlayingId(f.id)}
                    >
                      <img src={f.thumb} alt={f.label} />
                      <span className="se-featured__play" />
                    </button>
                  )}
                </div>
                <div className="se-featured__caption">
                  <span className="se-featured__title">{f.label}</span>
                  <span className="se-featured__desc">{kr ? f.subKr : f.subEn}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SectionExpertise;
